import {
  CounterElementWrapper,
  CounterLabel,
  CounterValue,
  Display,
  Separator,
} from "./InvoiceCounter.styles";
import { useInvoiceStatistics } from "../../hooks"
import { useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";

// Props for individual counter elements
interface CounterElementProps {
  label: string;
  value: number;
  warning: boolean;
  isActive?: boolean;
}

// Props for the main InvoiceCounter component
interface InvoiceCounterProps {
  invoiceCounts: {
    invoice_count: number;
    today: number;
    oneToSevenDays: number;
    eightToFifteenDays: number;
    sixteenPlusDays: number;
    outOfSLA: number;
  };
  resetActiveFilter: () => void;
  clearResultsClicked: boolean;
  setDateFilter: (filter: any) => void;
  setLastFilterSource: (source: string | null) => void;
  lastFilterSource: string | null; //  Add this
  invFilters:object;
}

/**
 * Renders a single counter element with optional click functionality and styling.
 */
function InvoiceCounterElement(props: CounterElementProps) {
  return (
    <CounterElementWrapper>
      <CounterLabel
        style={{
          background: props.isActive ? "#3A3D9B" : "transparent",
          borderRadius: "4px",
          padding: "0",
        }}
      >
        {props.label}
      </CounterLabel>
      {(
        <CounterValue warning={props.warning}>{props.value}</CounterValue>
      )}
    </CounterElementWrapper>
  );
}


function adjustForWeekend(date: string): string {
  const day = dayjs(date);
  const dayOfWeek = day.day();

  if (dayOfWeek === 6) {
    return day.add(2, "day").format("YYYY-MM-DD"); // Saturday → Monday
  } else if (dayOfWeek === 0) {
    return day.add(1, "day").format("YYYY-MM-DD"); // Sunday → Monday
  }

  return date; // Weekday remains unchanged
}

/**
 * Helper function to calculate a date by subtracting a number of working days.
 */
function subtractWorkingDays(date: dayjs.Dayjs, workingDays: number): string {
  let result = date.clone();
  while (workingDays > 0) {
    result = result.subtract(1, "day");
    const dayOfWeek = result.day();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays--;
    }
  }
  return adjustForWeekend(result.format("YYYY-MM-DD")); // 🔥 Apply weekend adjustment here
}

/**
 * Main component for rendering invoice counters and managing filter interactions.
 */
export default function InvoiceCounter({
  invoiceCounts,
  resetActiveFilter,
  clearResultsClicked,
  setDateFilter,
  setLastFilterSource,
  lastFilterSource, //  Add this here
  invFilters,
}: InvoiceCounterProps) {

  
  const [activeFilter, setActiveFilter] = useState<string | null>(
    () => localStorage.getItem("activeInvoiceCounterFilter") ?? null
  );
  const { data } = useInvoiceStatistics(-1, invFilters);

  const today = useMemo(() => adjustForWeekend(dayjs().format("YYYY-MM-DD")), []);
  const oneToSevenDays = useMemo(() => ({
    type: "range",
    start: subtractWorkingDays(dayjs(), 7),
    end: subtractWorkingDays(dayjs(), 1),
  }), []);

  const eightToFifteenDays = useMemo(() => ({
    type: "range",
    start: subtractWorkingDays(dayjs(), 15),
    end: subtractWorkingDays(dayjs(), 8),
  }), []);

  const sixteenPlusDays = useMemo(() => ({
    type: "before",
    value: subtractWorkingDays(dayjs(), 16),
  }), []);

  const outOfSLA = useMemo(() => ({
    type: "before",
    value: subtractWorkingDays(dayjs(), 3),
  }), []);

  useEffect(() => {
    // 🔹 Restore filter from localStorage when component mounts
    const storedFilter = localStorage.getItem("activeInvoiceCounterFilter");
    if (storedFilter) {
      setActiveFilter(storedFilter);
  
      const filterMapping = {
        receivedTodayFilter: { type: "single", value: today },
        oneToSevenDaysFilter: oneToSevenDays,
        eightToFifteenDaysFilter: eightToFifteenDays,
        sixteenPlusDaysFilter: sixteenPlusDays,
        outOfSLAFilter: outOfSLA,
      };
  
      setDateFilter(filterMapping[storedFilter] || null);
      setLastFilterSource("counterClick"); //  Ensure it knows the filter came from a UI number
    }
  }, []); //  Run **only once on mount** to restore selection
  
  useEffect(() => {
    if (!activeFilter) return;
  
    localStorage.setItem("activeInvoiceCounterFilter", activeFilter);
  
    const filterMapping = {
      receivedTodayFilter: { type: "single", value: today },
      oneToSevenDaysFilter: oneToSevenDays,
      eightToFifteenDaysFilter: eightToFifteenDays,
      sixteenPlusDaysFilter: sixteenPlusDays,
      outOfSLAFilter: outOfSLA,
    };    
  
    setDateFilter(filterMapping[activeFilter]);
  }, [activeFilter, setDateFilter, today, oneToSevenDays, eightToFifteenDays, sixteenPlusDays, outOfSLA]);
  

  const [localInvoiceCounts, setLocalInvoiceCounts] = useState(invoiceCounts);

  useEffect(() => {
    const storedCounts = localStorage.getItem("persistedInvoiceCounts");
    if (storedCounts) {
      setLocalInvoiceCounts(JSON.parse(storedCounts));
    }

    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key === "persistedInvoiceCounts" && event.newValue) {
        setLocalInvoiceCounts(JSON.parse(event.newValue)); //  Sync updates from other tabs
      }
    };

    window.addEventListener("storage", handleStorageUpdate);
    return () => window.removeEventListener("storage", handleStorageUpdate);
  }, []);

  useEffect(() => {
    const handleInvoiceCountUpdate = () => {
      const storedCounts = localStorage.getItem("persistedInvoiceCounts");
      if (storedCounts) {
        setLocalInvoiceCounts(JSON.parse(storedCounts));
      }
    };
  
    // 🔹 Listen for invoice count updates
    window.addEventListener("invoiceCountsUpdated", handleInvoiceCountUpdate);
    
    return () => {
      window.removeEventListener("invoiceCountsUpdated", handleInvoiceCountUpdate);
    };
  }, []);
  

  /**
   * Resets active filters when Clear Results is clicked.
   */
  useEffect(() => {
    if (clearResultsClicked) {
      if (lastFilterSource !== "counterClick") {
        setDateFilter(null);
        setLastFilterSource(null);
      }
    }
  }, [clearResultsClicked, setDateFilter, setLastFilterSource]);  
  
  //  Restore `setDateFilter` when the page reloads
  useEffect(() => {
    if (!activeFilter) return;
    
    const storedFilter = localStorage.getItem("activeInvoiceCounterFilter");
    if (storedFilter === activeFilter) return; //  Prevent redundant updates
  
    localStorage.setItem("activeInvoiceCounterFilter", activeFilter);
  
    const filterMapping = {
      receivedTodayFilter: { type: "single", value: today },
      oneToSevenDaysFilter: oneToSevenDays,
      eightToFifteenDaysFilter: eightToFifteenDays,
      sixteenPlusDaysFilter: sixteenPlusDays,
      outOfSLAFilter: outOfSLA,
    };
  
    setDateFilter(filterMapping[activeFilter]);
  }, [activeFilter, setDateFilter, today, oneToSevenDays, eightToFifteenDays, sixteenPlusDays, outOfSLA]);  

  

  return (
    <Display>
      <InvoiceCounterElement label={"Invoice Count"} value={data?.invoice_count} warning={false} />
      <Separator />

      <InvoiceCounterElement
        label={"Received Today"}
        value={data?.today}
        isActive={activeFilter === "receivedTodayFilter"}
        warning={false}
      />

      <InvoiceCounterElement
        label={"1-7 days"}
        value={data?.oneToSevenDays}
        isActive={activeFilter === "oneToSevenDaysFilter"}
        warning={false}
      />

      <InvoiceCounterElement
        label={"8-15 days"}
        value={data?.eightToFifteenDays}
        isActive={activeFilter === "eightToFifteenDaysFilter"}
        warning={false}
      />

      <InvoiceCounterElement
        label={"16+ days"}
        value={data?.sixteenPlusDays}
        isActive={activeFilter === "sixteenPlusDaysFilter"}
        warning={false}
      />
      < Separator />
      <InvoiceCounterElement
        label={"Out of SLA"}
        value={data?.outOfSLA}
        isActive={activeFilter === "outOfSLAFilter"}
        warning={true}
      />

    </Display>
  );
}
